import React, { useState } from "react";
import axios from "axios";
import {
  Ticket, Search, Trash2, X, Send, RefreshCw,
  MessageSquare, Edit3, Plus
} from "lucide-react";
import { useAdmin } from "../admin-context";
import { apiUrl } from "../../../utils/config";

const STATUS_COLORS = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  resolved:    "bg-green-100 text-green-700",
  closed:      "bg-gray-100 text-gray-500",
};
const PRIORITY_COLORS = {
  low:    "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-600",
  high:   "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-700",
};

const Badge = ({ text, colorMap }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${colorMap[text] || "bg-gray-100 text-gray-500"}`}>
    {text?.replace("_", " ")}
  </span>
);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

// ── Update Drawer ─────────────────────────────────────────────
function UpdateDrawer({ ticket, onClose, onSaved }) {
  const { addTicketMessage } = useAdmin();

  const [status, setStatus]     = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority);
  const [message, setMessage]   = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      // Always update status/priority
      await axios.put(`${apiUrl}/tickets/${ticket.id}`, { status, priority });

      // Send reply if message provided
      if (message.trim()) {
        await addTicketMessage(ticket.id, {
          sender_type: "admin",
          sender_name: "Support Team",
          message: message.trim(),
        });
      }
      onSaved();
    } catch (e) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "block text-xs font-bold text-gray-500 mb-1.5";
  const selectCls = "w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300";

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Slide-over drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <p className="text-xs font-mono text-gray-400 mb-0.5">{ticket.ticket_number}</p>
            <h2 className="text-lg font-extrabold text-gray-900 leading-tight">{ticket.subject}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{ticket.customer_name} · {ticket.customer_email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Conversation</p>
          {(ticket.messages || []).map((msg, i) => {
            const isAdmin = msg.sender_type === "admin";
            return (
              <div key={msg.id || i} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                  isAdmin ? "bg-orange-500 text-white rounded-br-sm" : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm"
                }`}>
                  <p className={`text-[11px] font-semibold mb-1 ${isAdmin ? "text-orange-100" : "text-gray-400"}`}>
                    {msg.sender_name} · {isAdmin ? "Support" : "Customer"}
                  </p>
                  <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                  <p className={`text-[10px] mt-1.5 ${isAdmin ? "text-orange-200" : "text-gray-400"}`}>{fmtDate(msg.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Update Form */}
        <div className="p-6 border-t border-gray-100 bg-white space-y-4">
          <p className="text-sm font-extrabold text-gray-700">Update Ticket</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className={selectCls}>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className={selectCls}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Reply Message <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Type a reply to send to the customer…"
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 bg-orange-500 text-white rounded-xl text-sm font-extrabold hover:bg-orange-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              {saving ? "Saving…" : <><Send size={14} /> Save Update</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Create Drawer ─────────────────────────────────────────────
const BLANK = { subject:"", category:"general", priority:"medium", description:"" };

function CreateDrawer({ onClose, onCreated }) {
  const { users } = useAdmin();

  const [form,           setForm]           = useState(BLANK);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dropdownOpen,   setDropdownOpen]   = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState("");

  const h = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const labelCls  = "block text-xs font-bold text-gray-500 mb-1.5";
  const inputCls  = "w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300";
  const selectCls = inputCls;

  const filteredCustomers = (users || []).filter(u => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const pickCustomer = (u) => {
    setSelectedCustomer(u);
    setCustomerSearch("");
    setDropdownOpen(false);
  };

  const handleCreate = async () => {
    if (!selectedCustomer) { setError("Please select a customer."); return; }
    if (!form.subject || !form.description) { setError("Subject and description are required."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${apiUrl}/tickets`, {
        customer_id:    selectedCustomer.id,
        customer_name:  selectedCustomer.name,
        customer_email: selectedCustomer.email,
        ...form,
      });
      if (res.data.status) {
        onCreated();
      } else {
        setError(res.data.error || "Failed to create ticket.");
      }
    } catch (_) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-extrabold text-gray-900">Create New Ticket</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 p-6 space-y-4">

          {/* Customer picker */}
          <div>
            <label className={labelCls}>Customer *</label>
            {selectedCustomer ? (
              <div className="flex items-center justify-between px-4 py-2.5 border border-orange-300 bg-orange-50 rounded-xl">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selectedCustomer.name}</p>
                  <p className="text-xs text-gray-500">{selectedCustomer.email} · ID #{selectedCustomer.id}</p>
                </div>
                <button onClick={() => setSelectedCustomer(null)} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setDropdownOpen(true); }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder="Search by name or email…"
                  className={inputCls + " pl-8"}
                />
                {dropdownOpen && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-400">No customers found</p>
                    ) : filteredCustomers.map(u => (
                      <button key={u.id} onMouseDown={() => pickCustomer(u)}
                        className="w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-0">
                        <p className="text-sm font-medium text-gray-900">{u.name}</p>
                        <p className="text-xs text-gray-400">{u.email} · ID #{u.id}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Subject *</label>
            <input name="subject" value={form.subject} onChange={h} placeholder="Brief description of the issue" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select name="category" value={form.category} onChange={h} className={selectCls}>
                <option value="general">General</option>
                <option value="order">Order Issue</option>
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <select name="priority" value={form.priority} onChange={h} className={selectCls}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Description *</label>
            <textarea name="description" value={form.description} onChange={h} rows={5}
              placeholder="Describe the issue in detail…"
              className={inputCls + " resize-none"}
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="flex-1 py-3 bg-orange-500 text-white rounded-xl text-sm font-extrabold hover:bg-orange-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? "Creating…" : <><Plus size={14} /> Create Ticket</>}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function TicketsPage() {
  const { tickets, loadTickets, deleteTicket } = useAdmin();

  const [statusFilter,   setStatusFilter]   = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [search,         setSearch]         = useState("");
  const [drawerTicket,   setDrawerTicket]   = useState(null);
  const [createOpen,     setCreateOpen]     = useState(false);
  const [loadingId,      setLoadingId]      = useState(null);
  const [deleteConfirm,  setDeleteConfirm]  = useState(null);

  const filtered = (tickets || []).filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        t.customer_name?.toLowerCase().includes(q) ||
        t.customer_email?.toLowerCase().includes(q) ||
        t.subject?.toLowerCase().includes(q) ||
        t.ticket_number?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const openDrawer = async (t) => {
    setLoadingId(t.id);
    try {
      const res = await axios.get(`${apiUrl}/tickets/${t.id}`);
      if (res.data.status) setDrawerTicket(res.data.ticket);
    } catch (_) {}
    setLoadingId(null);
  };

  const handleSaved = async () => {
    // Refresh thread + list
    if (drawerTicket) {
      const res = await axios.get(`${apiUrl}/tickets/${drawerTicket.id}`);
      if (res.data.status) setDrawerTicket(res.data.ticket);
    }
    await loadTickets();
  };

  const confirmDelete = async () => {
    await deleteTicket(deleteConfirm.id);
    if (drawerTicket?.id === deleteConfirm.id) setDrawerTicket(null);
    setDeleteConfirm(null);
  };

  const counts = {
    all:         (tickets || []).length,
    open:        (tickets || []).filter(t => t.status === "open").length,
    in_progress: (tickets || []).filter(t => t.status === "in_progress").length,
    resolved:    (tickets || []).filter(t => t.status === "resolved").length,
    closed:      (tickets || []).filter(t => t.status === "closed").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Support Tickets</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.open} open · {counts.in_progress} in progress · {counts.resolved} resolved
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadTickets} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors" title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors"
          >
            <Plus size={15} /> Create Ticket
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, subject, ticket #…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none text-gray-600 bg-white">
            <option value="all">All priorities</option>
            {["low","medium","high","urgent"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all","open","in_progress","resolved","closed"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-full font-semibold transition-colors capitalize ${
                statusFilter === s ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s === "all" ? `All (${counts.all})` : `${s.replace("_"," ")} (${counts[s] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {/* Ticket Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Ticket size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No tickets found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Ticket</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Customer</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Category</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Priority</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Opened</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-[11px] font-mono text-gray-400 mb-0.5">{t.ticket_number}</p>
                    <p className="font-semibold text-gray-900 truncate max-w-[220px]">{t.subject}</p>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <p className="font-medium text-gray-800">{t.customer_name}</p>
                    <p className="text-xs text-gray-400">{t.customer_email}</p>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className="capitalize text-gray-600">{t.category}</span>
                  </td>
                  <td className="px-5 py-4">
                    <Badge text={t.status} colorMap={STATUS_COLORS} />
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <Badge text={t.priority} colorMap={PRIORITY_COLORS} />
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell text-xs text-gray-400">
                    {fmtDate(t.created_at)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openDrawer(t)}
                        disabled={loadingId === t.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-colors"
                      >
                        {loadingId === t.id ? "…" : <><Edit3 size={12} /> Update</>}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(t)}
                        className="p-1.5 rounded-xl hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Drawer */}
      {createOpen && (
        <CreateDrawer
          onClose={() => setCreateOpen(false)}
          onCreated={async () => { await loadTickets(); setCreateOpen(false); }}
        />
      )}

      {/* Update Drawer */}
      {drawerTicket && (
        <UpdateDrawer
          ticket={drawerTicket}
          onClose={() => setDrawerTicket(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-80">
            <h3 className="font-extrabold text-gray-900 mb-2">Delete Ticket?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Delete <span className="font-semibold">{deleteConfirm.ticket_number}</span> and all its messages? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
